import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase-config";
import { HostInfoModal } from "../../Components/Modals/HostInfoModal";
import { FaLocationPin, FaXTwitter } from "react-icons/fa6";
import {
  FaCalendar,
  FaShare,
  FaEdit,
  FaUsers,
  FaFacebook,
  FaWhatsapp,
  FaLinkedin,
  FaEnvelope,
  FaCopy,
  FaInstagram,
  FaTags,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaQuoteLeft,
  FaDirections,
  FaClock,
} from "react-icons/fa";
import { IoBag, IoNavigate } from "react-icons/io5";
import {
  Avatar,
  Container,
  Flex,
  Grid,
  GridItem,
  Icon,
  Image,
  Text,
  Button,
  Divider,
  useToast,
  IconButton,
  Tooltip,
  useDisclosure,
  Box,
  VStack,
  HStack,
  Badge,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Input,
  InputGroup,
  InputRightElement,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagLeftIcon,
  Skeleton,
  SkeletonCircle,
} from "@chakra-ui/react";
import { GoogleMapItem } from "../../Components/Structure/map";
import { TicketArea } from "../../Components/Structure/ticketArea";
import { RichDescription } from "../../Components/Structure/RichTextEditor";
import { CreateEventModal } from "../../Components/Modals";

const YourHost = ({ host }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <HStack
        as="button"
        type="button"
        onClick={onOpen}
        spacing={3}
        bg="linear-gradient(135deg, #F8FAFF 0%, #F3F8FE 100%)"
        p={3}
        borderRadius="12px"
        border="1px solid"
        borderColor="blue.100"
        maxW={["100%", "100%", "fit-content"]}
        cursor="pointer"
        transition="all 0.2s"
        _hover={{
          borderColor: "blue.300",
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(80, 140, 247, 0.15)",
        }}
        _active={{ transform: "translateY(0)" }}
      >
        <Avatar
          size="md"
          name={host?.displayName}
          src={host?.avatarUrl}
          border="2px solid"
          borderColor="blue.400"
        />
        <VStack align="start" spacing={0} flex="1">
          <Text
            fontSize="xs"
            fontWeight="600"
            color="blue.500"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            Hosted by
          </Text>
          <Text fontSize="md" fontWeight="700" color="gray.800">
            {host?.displayName}
          </Text>
        </VStack>
        <Icon as={FaChevronRight} color="blue.400" boxSize={3} />
      </HStack>

      <HostInfoModal isOpen={isOpen} onClose={onClose} host={host} />
    </>
  );
};

const MainInfo = ({ event }) => {
  const [formattedTime, setFormattedTime] = useState("");
  const [formattedDate, setFormattedDate] = useState("");

  function formatDate(timestamp) {
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  }

  function formatTime(timestamp) {
    const time = new Date(timestamp.seconds * 1000);
    return time.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  useEffect(() => {
    if (event && event.startTime) {
      const formattedStartDate = formatDate(event.startTime);
      const startTime = formatTime(event.startTime);
      let times = startTime;

      if (event.endTime) {
        const endTime = formatTime(event.endTime);
        times += ` - ${endTime}`;
      }

      setFormattedDate(formattedStartDate);
      setFormattedTime(times);
    }
  }, [event]);

  const InfoCard = ({ icon, label, value, subValue }) => (
    <HStack
      spacing={3}
      p={3}
      bg="gray.50"
      borderRadius="12px"
    >
      <Flex
        boxSize="44px"
        borderRadius="10px"
        bg="white"
        alignItems="center"
        justifyContent="center"
        boxShadow="sm"
      >
        <Icon as={icon} fontSize="20px" color="#508cf7" />
      </Flex>
      <VStack align="start" spacing={0} flex={1}>
        <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="700" color="gray.800" noOfLines={2}>
          {value}
        </Text>
        {subValue && (
          <Text fontSize="xs" fontWeight="600" color="gray.600">
            {subValue}
          </Text>
        )}
      </VStack>
    </HStack>
  );

  return (
    <VStack spacing={2} py={3} align="stretch">
      <InfoCard
        icon={FaLocationPin}
        label="Location"
        value={event.location}
      />
      <InfoCard
        icon={FaCalendar}
        label="Date & Time"
        value={formattedDate}
        subValue={formattedTime}
      />
      {event.guestsToBring && (
        <InfoCard
          icon={IoBag}
          label="What to Bring"
          value={event.guestsToBring}
        />
      )}
    </VStack>
  );
};

// Share Drawer Component
const ShareDrawer = ({ eventId, eventName, eventImage }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  // Use /e/ short URL for sharing (has OG tags for social media previews)
  const shareUrl = `https://app.proximy.ca/e/${eventId}`;
  const shareText = `Check out "${eventName}" on Proximy!`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast({
        title: "Link copied!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    });
  };

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: FaWhatsapp,
      bgColor: "#25D366",
      action: () => {
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, "_blank");
        onClose();
      },
    },
    {
      name: "Facebook",
      icon: FaFacebook,
      bgColor: "#1877F2",
      action: () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, "_blank");
        onClose();
      },
    },
    {
      name: "X",
      icon: FaXTwitter,
      bgColor: "#000000",
      action: () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, "_blank");
        onClose();
      },
    },
    {
      name: "LinkedIn",
      icon: FaLinkedin,
      bgColor: "#0A66C2",
      action: () => {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, "_blank");
        onClose();
      },
    },
    {
      name: "Email",
      icon: FaEnvelope,
      bgColor: "#EA4335",
      action: () => {
        const subject = encodeURIComponent(`Join me at: ${eventName}`);
        const body = encodeURIComponent(`Hey!\n\nI found this event and thought you might be interested:\n\n${eventName}\n\n${shareUrl}\n\nSee you there!`);
        window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
        onClose();
      },
    },
    {
      name: "Instagram",
      icon: FaInstagram,
      bgColor: "#E4405F",
      action: () => {
        navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`).then(() => {
          toast({
            title: "Copied for Instagram!",
            description: "Paste in your Stories or bio",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        });
        onClose();
      },
    },
  ];

  return (
    <>
      <Button
        leftIcon={<Icon as={FaShare} boxSize={3} />}
        size="sm"
        bg="white"
        color="gray.700"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="full"
        px={4}
        fontWeight="600"
        fontSize="xs"
        onClick={onOpen}
        _hover={{
          bg: "gray.50",
          borderColor: "gray.300",
          transform: "translateY(-1px)",
          boxShadow: "sm"
        }}
        _active={{ transform: "translateY(0)" }}
        transition="all 0.2s"
      >
        Share
      </Button>

      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent
          borderTopRadius="20px"
          maxH="85vh"
          pb="env(safe-area-inset-bottom)"
          m={0}
        >
          <Box w="36px" h="4px" bg="gray.300" borderRadius="full" mx="auto" mt={2} />
          <DrawerCloseButton top={3} right={4} />

          <DrawerHeader px={4} pb={0} pt={4}>
            <Text fontSize="lg" fontWeight="bold">Share Event</Text>
          </DrawerHeader>

          <DrawerBody px={4} pt={4} pb={6}>
            {/* Event Preview Card */}
            <Box
              bg="gray.50"
              borderRadius="12px"
              p={3}
              mb={5}
            >
              <HStack spacing={3}>
                <Image
                  src={eventImage}
                  alt={eventName}
                  w="50px"
                  h="50px"
                  borderRadius="10px"
                  objectFit="cover"
                />
                <Box flex={1}>
                  <Text fontWeight="600" fontSize="sm" noOfLines={2}>
                    {eventName}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={0.5}>
                    app.proximy.ca
                  </Text>
                </Box>
              </HStack>
            </Box>

            {/* Copy Link Section */}
            <Box mb={5}>
              <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2} textTransform="uppercase">
                Event Link
              </Text>
              <InputGroup size="md">
                <Input
                  value={shareUrl}
                  isReadOnly
                  bg="white"
                  borderRadius="10px"
                  fontSize="sm"
                  pr="4.5rem"
                  color="gray.600"
                />
                <InputRightElement width="4rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={handleCopyLink}
                    colorScheme="blue"
                    borderRadius="6px"
                    fontSize="xs"
                  >
                    Copy
                  </Button>
                </InputRightElement>
              </InputGroup>
            </Box>

            {/* Share Options */}
            <Box>
              <Text fontSize="xs" fontWeight="600" color="gray.500" mb={3} textTransform="uppercase">
                Share via
              </Text>
              <HStack spacing={5} justify="flex-start" flexWrap="wrap">
                {shareOptions.map((option) => (
                  <VStack
                    key={option.name}
                    spacing={1.5}
                    cursor="pointer"
                    onClick={option.action}
                    role="group"
                  >
                    <Flex
                      w="48px"
                      h="48px"
                      borderRadius="14px"
                      bg={option.bgColor}
                      alignItems="center"
                      justifyContent="center"
                      transition="all 0.15s"
                      _groupHover={{ transform: "scale(1.05)" }}
                    >
                      <Icon as={option.icon} color="white" boxSize={5} />
                    </Flex>
                    <Text fontSize="11px" color="gray.600" fontWeight="500">
                      {option.name}
                    </Text>
                  </VStack>
                ))}
              </HStack>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// Interest Icon with Skeleton
const InterestIcon = ({ icon, name }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <Box mr={1.5} display="flex" alignItems="center">
      <SkeletonCircle
        size="16px"
        isLoaded={isLoaded}
        fadeDuration={0.4}
      >
        <Image
          src={icon}
          alt={name}
          boxSize="16px"
          objectFit="contain"
          onLoad={() => setIsLoaded(true)}
          borderRadius="full"
        />
      </SkeletonCircle>
    </Box>
  );
};

// Interests Section Component
const InterestsSection = ({ interests }) => {
  const [interestsData, setInterestsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  useEffect(() => {
    const fetchInterests = async () => {
      if (!interests || interests.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const interestPromises = interests.map(async (interestRef) => {
          if (interestRef && interestRef.id) {
            const interestDoc = await getDoc(doc(db, "interests", interestRef.id));
            if (interestDoc.exists()) {
              return { id: interestDoc.id, ...interestDoc.data() };
            }
          }
          return null;
        });

        const results = await Promise.all(interestPromises);
        setInterestsData(results.filter(Boolean));
      } catch (error) {
        console.error("Error fetching interests:", error);
      }
      setLoading(false);
    };

    fetchInterests();
  }, [interests]);

  if (!interests || interests.length === 0) return null;

  const tagColors = [
    { bg: "blue.50", color: "blue.600", borderColor: "blue.200" },
    { bg: "purple.50", color: "purple.600", borderColor: "purple.200" },
    { bg: "teal.50", color: "teal.600", borderColor: "teal.200" },
    { bg: "orange.50", color: "orange.600", borderColor: "orange.200" },
    { bg: "pink.50", color: "pink.600", borderColor: "pink.200" },
    { bg: "green.50", color: "green.600", borderColor: "green.200" },
    { bg: "cyan.50", color: "cyan.600", borderColor: "cyan.200" },
  ];

  return (
    <Box mt={4}>
      <HStack
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        py={2}
        _hover={{ opacity: 0.8 }}
        transition="all 0.2s"
      >
        <HStack spacing={2}>
          <Icon as={FaTags} color="gray.500" boxSize={3} />
          <Text fontSize="12px" fontWeight="600" color="gray.600">
            Interests
          </Text>
          <Badge
            colorScheme="blue"
            fontSize="10px"
            borderRadius="full"
            px={2}
          >
            {interestsData.length}
          </Badge>
        </HStack>
        <Icon
          as={isOpen ? FaChevronUp : FaChevronDown}
          color="gray.400"
          boxSize={3}
        />
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <Box pt={2} pb={1}>
          {loading ? (
            <Text fontSize="sm" color="gray.500">Loading...</Text>
          ) : (
            <Wrap spacing={2}>
              {interestsData.map((interest, index) => {
                const colorScheme = tagColors[index % tagColors.length];
                return (
                  <WrapItem key={interest.id}>
                    <Tag
                      size="md"
                      borderRadius="full"
                      bg={colorScheme.bg}
                      color={colorScheme.color}
                      border="1px solid"
                      borderColor={colorScheme.borderColor}
                      px={3}
                      py={1.5}
                      fontWeight="500"
                    >
                      {interest.icon && (
                        <InterestIcon icon={interest.icon} name={interest.name} />
                      )}
                      <TagLabel fontSize="xs">
                        {interest.name || interest.id}
                      </TagLabel>
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// Attendees Section Component (only for event owner)
const AttendeesSection = ({ members, isOwner }) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profilePerson, setProfilePerson] = useState(null);
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  useEffect(() => {
    const fetchAttendees = async () => {
      if (!members || members.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const attendeePromises = members.map(async (memberRef) => {
          if (memberRef && memberRef.id) {
            const memberDoc = await getDoc(doc(db, "users", memberRef.id));
            if (memberDoc.exists()) {
              return { id: memberDoc.id, ...memberDoc.data() };
            }
          }
          return null;
        });

        const results = await Promise.all(attendeePromises);
        setAttendees(results.filter(Boolean));
      } catch (error) {
        console.error("Error fetching attendees:", error);
      }
      setLoading(false);
    };

    fetchAttendees();
  }, [members]);

  if (!isOwner) return null;

  return (
    <Box mt={6} p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
      <HStack justify="space-between" cursor="pointer" onClick={onToggle}>
        <HStack>
          <Icon as={FaUsers} color="blue.600" />
          <Text fontWeight="bold" color="blue.700">
            Attendees ({attendees.length})
          </Text>
        </HStack>
        <Icon
          as={isOpen ? FaChevronUp : FaChevronDown}
          color="blue.500"
          boxSize={3}
        />
      </HStack>

      <Collapse in={isOpen}>
        <VStack align="stretch" mt={4} spacing={2}>
          {loading ? (
            <Text fontSize="sm" color="gray.500">Loading attendees...</Text>
          ) : attendees.length === 0 ? (
            <Text fontSize="sm" color="gray.500">No attendees yet</Text>
          ) : (
            attendees.map((attendee, index) => (
              <HStack
                key={attendee.id}
                p={2}
                bg="white"
                borderRadius="md"
                justify="space-between"
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
                onClick={() => setProfilePerson(attendee)}
              >
                <HStack>
                  <Avatar size="sm" name={attendee.displayName} src={attendee.avatarUrl} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600">
                      {attendee.displayName || "Anonymous"}
                      {index === 0 && (
                        <Badge ml={2} colorScheme="purple" fontSize="xs">Host</Badge>
                      )}
                    </Text>
                    <Text fontSize="xs" color="gray.500">{attendee.email || attendee.phoneNumber || ""}</Text>
                  </Box>
                </HStack>
              </HStack>
            ))
          )}
        </VStack>
      </Collapse>
      <HostInfoModal
        isOpen={!!profilePerson}
        onClose={() => setProfilePerson(null)}
        host={profilePerson}
        showHostedBy={false}
      />
    </Box>
  );
};

export const EventView = ({ user }) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { eventId } = useParams();
  const toast = useToast();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();

  const fetchEventById = async (rawId) => {
    setLoading(true);
    try {
      let docSnap = null;

      // Try 1: Fetch using raw Firestore doc ID (new format)
      const rawDocRef = doc(db, "events", rawId);
      docSnap = await getDoc(rawDocRef);

      // Try 2: If not found, try with [#...] format (old custom ID format)
      if (!docSnap.exists()) {
        const formattedId = `[#${rawId}]`;
        const formattedDocRef = doc(db, "events", formattedId);
        docSnap = await getDoc(formattedDocRef);
      }

      if (docSnap.exists()) {
        const eventData = docSnap.data();
        setEvent({ id: docSnap.id, ...eventData });

        if (
          eventData.members &&
          eventData.members.length > 0 &&
          eventData.members[0].id
        ) {
          const memberRef = doc(db, "users", eventData.members[0].id);
          const memberSnap = await getDoc(memberRef);
          if (memberSnap.exists()) {
            setEvent((current) => ({
              ...current,
              memberData: memberSnap.data(),
            }));
          }
        }
      } else {
        console.log("No event found with ID:", rawId);
      }
    } catch (error) {
      console.error("Error fetching event or member:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (eventId) {
      const cleanedId = eventId.replace(/^%23/, "").replace(/^#/, "");
      fetchEventById(cleanedId);
    }
  }, [eventId]);

  const handleEdit = () => {
    onEditModalOpen();
  };

  const handleEventUpdated = () => {
    // Refetch the event data after update
    if (eventId) {
      const cleanedId = eventId.replace(/^%23/, "").replace(/^#/, "");
      fetchEventById(cleanedId);
    }
  };

  const isOwner = user && event && event.host === user.uid;

  // useEffect(() => {
  //   let finalId = "";

  //   const decodedURL = decodeURIComponent(window.location.href);

  //   // Case 1: Check if there's a hash
  //   if (decodedURL.includes("#")) {
  //     const rawId = decodedURL.split("#")[1];
  //     finalId = rawId.split("?")[0]; // remove fbclid or other params
  //   }

  //   if (finalId) {
  //     fetchEventById(finalId);
  //   } else {
  //     console.error("No valid ID found.");
  //     setLoading(false);
  //   }
  // }, []);

  if (loading) {
    return (
      <Container maxW="container.lg" p="1rem">
        <Grid
          gap={6}
          templateColumns={["100%", "100%", "38% 62%"]}
          justifyItems="start"
        >
          <GridItem w="100%">
            <Skeleton borderRadius="16px" height="300px" />
            <HStack mt={4} spacing={2}>
              <Skeleton borderRadius="full" height="24px" width="80px" />
              <Skeleton borderRadius="full" height="24px" width="100px" />
              <Skeleton borderRadius="full" height="24px" width="70px" />
            </HStack>
          </GridItem>
          <GridItem w="100%">
            <Skeleton borderRadius="12px" height="60px" width="200px" mb={4} />
            <Skeleton height="40px" width="80%" mb={2} />
            <Skeleton height="40px" width="60%" mb={6} />
            <VStack spacing={2} align="stretch">
              <Skeleton borderRadius="12px" height="70px" />
              <Skeleton borderRadius="12px" height="70px" />
              <Skeleton borderRadius="12px" height="70px" />
            </VStack>
            <Skeleton borderRadius="12px" height="120px" mt={6} />
            <Skeleton borderRadius="16px" height="200px" mt={6} />
          </GridItem>
        </Grid>
      </Container>
    );
  }


  // HTML parable event description system
  // Discover events system

  if (!event) {
    return (
      <Container maxW="container.lg" p="1rem">
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="50vh"
          textAlign="center"
        >
          <Text fontSize="xl" fontWeight="600" color="gray.600" mb={2}>
            Event not found
          </Text>
          <Text fontSize="sm" color="gray.500">
            This event may have been removed or the link is incorrect.
          </Text>
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" p={"1rem"} position="relative">
      {/* Floating Edit Button - Only for event owner */}
      {isOwner && (
        <Button
          onClick={handleEdit}
          position="fixed"
          bottom={{ base: "20px", md: "32px" }}
          right={{ base: "20px", md: "32px" }}
          zIndex={20}
          h="56px"
          pl="10px"
          pr={6}
          borderRadius="full"
          fontWeight="700"
          fontSize="sm"
          letterSpacing="-0.01em"
          color="white"
          bgGradient="linear(135deg, #6ea3ff 0%, #508cf7 45%, #226cf1 100%)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.35)"
          boxShadow="0 18px 38px -10px rgba(34,108,241,0.55), 0 8px 20px -8px rgba(80,140,247,0.45), inset 0 1px 0 rgba(255,255,255,0.35)"
          sx={{
            "& .fab-chip": {
              width: "38px",
              height: "38px",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
              backdropFilter: "blur(4px)",
              transition: "transform .35s ease, background .25s ease",
            },
          }}
          _hover={{
            transform: "translateY(-3px)",
            boxShadow:
              "0 26px 50px -12px rgba(34,108,241,0.65), 0 12px 24px -8px rgba(80,140,247,0.55), inset 0 1px 0 rgba(255,255,255,0.4)",
            "& .fab-chip": {
              background: "rgba(255,255,255,0.28)",
              transform: "rotate(-8deg) scale(1.05)",
            },
          }}
          _active={{
            transform: "translateY(-1px)",
            boxShadow:
              "0 10px 22px -8px rgba(34,108,241,0.55), inset 0 1px 0 rgba(255,255,255,0.35)",
          }}
          style={{ transition: "all 0.25s ease" }}
        >
          <Box className="fab-chip">
            <Icon as={FaEdit} boxSize="14px" />
          </Box>
          Edit Event
        </Button>
      )}

      <Grid
        gap={6}
        templateColumns={["100%", "100%", "38% 62%"]}
        justifyItems={"start"}
        textAlign={"left"}
      >
        <GridItem>
          <Box
            borderRadius="16px"
            overflow="hidden"
            boxShadow="lg"
          >
            <Image
              w="100%"
              maxH="400px"
              fit="cover"
              src={event.imageUrl}
              alt={event.name}
            />
          </Box>
          <InterestsSection interests={event.interests} />
        </GridItem>

        <GridItem w="full">
          <YourHost host={event.memberData} />

          <Flex alignItems="flex-start" justifyContent="space-between" mt={4} gap={3}>
            <Text
              fontWeight="800"
              fontSize={["1.8rem", "2rem", "2.2rem"]}
              textAlign="left"
              lineHeight="1.2"
              color="gray.800"
              flex={1}
            >
              {event.name}
            </Text>

            <ShareDrawer
              eventId={eventId}
              eventName={event.name}
              eventImage={event.imageUrl}
            />
          </Flex>

          <TicketArea event={event} user={user} />

          <MainInfo event={event} />

          {/* Description Section */}
          <Box mt={4}>
            <Text
              fontSize="xs"
              fontWeight="700"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={2}
            >
              About this Event
            </Text>
            <Box
              p={4}
              bg="white"
              borderRadius="12px"
              border="1px solid"
              borderColor="gray.100"
            >
              <RichDescription html={event.description} />
            </Box>
          </Box>

          {/* Map Section */}
          <Box
            mt={6}
            p={4}
            bg="gray.50"
            borderRadius="16px"
          >
            <HStack justify="space-between" mb={3}>
              <HStack spacing={2}>
                <Icon as={FaLocationPin} color="#508cf7" boxSize={4} />
                <Text fontSize="sm" fontWeight="700" color="gray.700">
                  Event Location
                </Text>
              </HStack>
              <Button
                size="sm"
                colorScheme="blue"
                variant="solid"
                leftIcon={<Icon as={IoNavigate} />}
                borderRadius="8px"
                onClick={() => {
                  const destination = encodeURIComponent(event.location);
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, "_blank");
                }}
              >
                Directions
              </Button>
            </HStack>
            <Text fontSize="sm" fontWeight="600" color="gray.600" mb={2}>
              {event.location}
            </Text>
            <Box borderRadius="12px" overflow="hidden" boxShadow="sm">
              <GoogleMapItem info={event} style={{ borderRadius: "12px" }} />
            </Box>
          </Box>

          {/* Attendees Section - Only visible to event owner */}
          <AttendeesSection members={event.members} isOwner={isOwner} />
        </GridItem>
      </Grid>

      {/* Subtle footer — legal links live here, quiet but always present. */}
      <Box
        position="relative"
        zIndex={1}
        borderTop="1px solid"
        borderColor="gray.100"
        bg="whiteAlpha.700"
        backdropFilter="blur(6px)"
      >
        <Container
          maxW="container.xl"
          px={{ base: "20px", md: "30px", lg: "60px" }}
          py={4}
        >
          <Flex
            direction={{ base: "column", sm: "row" }}
            align={{ base: "flex-start", sm: "center" }}
            justify="space-between"
            gap={2}
          >
            <Text fontSize="11px" color="gray.400">
              &copy; {new Date().getFullYear()} Proximy Technologies, Inc.
            </Text>
            <HStack spacing={4} color="gray.400" fontSize="11px">
              <Box
                as="a"
                href="https://proximy-site.web.app/terms"
                target="_blank"
                rel="noopener noreferrer"
                _hover={{ color: "#508cf7", textDecoration: "underline" }}
              >
                Terms
              </Box>
              <Box
                as="a"
                href="https://proximy-site.web.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                _hover={{ color: "#508cf7", textDecoration: "underline" }}
              >
                Privacy
              </Box>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Edit Event Modal */}
      {event && (
        <CreateEventModal
          isOpen={isEditModalOpen}
          onClose={onEditModalClose}
          user={user}
          event={event}
          onEventUpdated={handleEventUpdated}
        />
      )}
    </Container>
  );
};
